import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportPayload, ReportType } from '@finvox/report-engine';
import { ReportView } from '@/components/ReportView';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/contexts/AuthContext';
import { useOrg } from '@/contexts/OrgContext';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { formatWithHijri } from '@/lib/hijri';
import { downloadCsv, printReportPdf, reportHtmlForPrint } from '@/lib/report-export';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { usePaginatedPartySelect } from '@/hooks/usePaginatedPartySelect';
import { Pagination } from '@/components/ui/Pagination';

const BASE_REPORT_TYPES: { id: ReportType; labelKey: string }[] = [
  { id: 'trial_balance', labelKey: 'reports.types.trialBalance' },
  { id: 'general_ledger', labelKey: 'reports.types.generalLedger' },
  { id: 'profit_loss', labelKey: 'reports.types.profitLoss' },
  { id: 'balance_sheet', labelKey: 'reports.types.balanceSheet' },
  { id: 'cash_flow', labelKey: 'reports.types.cashFlow' },
  { id: 'equity_statement', labelKey: 'reports.types.equity' },
  { id: 'journal_register', labelKey: 'reports.types.journalRegister' },
  { id: 'ar_aging', labelKey: 'reports.types.arAging' },
  { id: 'ap_aging', labelKey: 'reports.types.apAging' },
  { id: 'profit_loss_by_cost_center', labelKey: 'reports.types.plByCc' },
  { id: 'fixed_asset_schedule', labelKey: 'reports.types.fixedAssets' },
  { id: 'payroll_summary', labelKey: 'reports.types.payrollSummary' },
  { id: 'e_invoice_register', labelKey: 'reports.types.eInvoiceRegister' },
  { id: 'bank_reconciliation', labelKey: 'reports.types.bankRecon' },
  { id: 'inventory_valuation', labelKey: 'reports.types.inventoryValuation' },
  { id: 'customer_statement', labelKey: 'reports.types.customerStatement' },
  { id: 'vendor_statement', labelKey: 'reports.types.vendorStatement' },
  { id: 'audit_export', labelKey: 'reports.types.auditExport' },
  { id: 'withholding_summary', labelKey: 'reports.types.withholding' },
  { id: 'tax_reconciliation', labelKey: 'reports.types.taxRecon' },
  { id: 'budget_vs_actual', labelKey: 'reports.types.budgetVsActual' },
  { id: 'depreciation_report', labelKey: 'reports.types.depreciationReport' },
  { id: 'sales_register', labelKey: 'reports.types.salesRegister' },
  { id: 'purchase_register', labelKey: 'reports.types.purchaseRegister' }
];

const VAT_BY_TEMPLATE: Record<string, ReportType> = {
  eg: 'vat_summary_eg',
  sa: 'vat_summary_sa',
  gcc: 'vat_summary_gcc',
  generic: 'vat_summary_gcc'
};

const VAT_LABEL_KEYS: Record<string, string> = {
  vat_summary_eg: 'reports.types.vatEg',
  vat_summary_sa: 'reports.types.vatSa',
  vat_summary_gcc: 'reports.types.vatGcc'
};

export function ReportsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { org, orgId, periods, currentPeriodId, skipLiveUpdate } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();
  const printRef = useRef<HTMLDivElement>(null);

  const period = periods.find((p) => p.id === currentPeriodId);
  const [showHijri, setShowHijri] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'regional'), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      setShowHijri(Boolean(snap.data()?.showHijri));
    });
  }, [orgId, skipLiveUpdate]);

  const reportTypes = useMemo(() => {
    const vat = VAT_BY_TEMPLATE[org?.countryTemplate ?? 'generic'];
    const vatEntry = vat
      ? [{ id: vat, labelKey: VAT_LABEL_KEYS[vat] ?? 'reports.types.vatGcc' }]
      : [];
    return [...BASE_REPORT_TYPES, ...vatEntry];
  }, [org?.countryTemplate]);
  const [reportType, setReportType] = useState<ReportType>('trial_balance');
  const [startDate, setStartDate] = useState(period?.startDate ?? '');
  const [endDate, setEndDate] = useState(period?.endDate ?? '');
  const [source, setSource] = useState<'all' | 'manual' | 'opening' | 'import'>('all');
  const [data, setData] = useState<ReportPayload | null>(null);
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [partyId, setPartyId] = useState('');
  const [glPage, setGlPage] = useState(1);
  const [glPageSize] = useState(10);
  const customerParties = usePaginatedPartySelect(orgId, 'customers');
  const vendorParties = usePaginatedPartySelect(orgId, 'vendors');
  const needsParty =
    reportType === 'customer_statement' || reportType === 'vendor_statement';
  const partyPage =
    reportType === 'customer_statement' ? customerParties : vendorParties;

  useEffect(() => {
    if (period) {
      setStartDate(period.startDate);
      setEndDate(period.endDate);
    }
  }, [period?.id, period?.startDate, period?.endDate]);

  useEffect(() => {
    if (!user) return;
    void getDoc(doc(getFirestoreDb(), 'users', user.uid)).then((snap) => {
      const fav = snap.data()?.favoriteReports;
      if (Array.isArray(fav)) setFavorites(fav.filter((x) => typeof x === 'string'));
    });
  }, [user]);

  const toggleFavorite = useCallback(async () => {
    if (!user) return;
    const key = reportType;
    const next = favorites.includes(key)
      ? favorites.filter((f) => f !== key)
      : [...favorites, key];
    setFavorites(next);
    await setDoc(
      doc(getFirestoreDb(), 'users', user.uid),
      { favoriteReports: next },
      { merge: true }
    );
  }, [user, reportType, favorites]);

  const generate = useCallback(
    async (pageOverride?: number) => {
      if (!orgId || !online || !startDate || !endDate) return;
      const page = pageOverride ?? (reportType === 'general_ledger' ? glPage : 1);
      setLoading(true);
      try {
        const res = await apiPost<{
          snapshotId: string;
          cached: boolean;
          data: ReportPayload;
        }>('/v1/reports/build', {
          orgId,
          reportType,
          params: {
            startDate,
            endDate,
            partyId: needsParty ? partyId : undefined,
            source: reportType === 'journal_register' ? source : undefined,
            page: reportType === 'general_ledger' ? page : undefined,
            pageSize: reportType === 'general_ledger' ? glPageSize : undefined
          }
        });
        setData(res.data);
        setCached(res.cached);
        if (reportType === 'general_ledger' && pageOverride) setGlPage(pageOverride);
      } catch (e) {
        toastError(toast, e, 'reports');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [
      orgId,
      online,
      startDate,
      endDate,
      reportType,
      needsParty,
      partyId,
      source,
      glPage,
      glPageSize,
      toast
    ]
  );

  function exportCsv() {
    if (!data) return;
    downloadCsv(`${reportType}-${endDate}.csv`, data);
  }

  function exportPdf() {
    if (!data || !org) return;
    const locale = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    const periodStart = formatWithHijri(startDate, showHijri, locale);
    const periodEnd = formatWithHijri(endDate, showHijri, locale);
    const titleKey =
      VAT_LABEL_KEYS[reportType] ??
      `reports.types.${reportType === 'trial_balance' ? 'trialBalance' : reportType === 'general_ledger' ? 'generalLedger' : reportType === 'profit_loss' ? 'profitLoss' : reportType === 'balance_sheet' ? 'balanceSheet' : reportType === 'cash_flow' ? 'cashFlow' : reportType === 'equity_statement' ? 'equity' : reportType === 'ar_aging' ? 'arAging' : reportType === 'ap_aging' ? 'apAging' : 'journalRegister'}`;
    const title = t(titleKey);
    printReportPdf({
      orgName: org.name,
      reportTitle: title,
      titleAr: t(`reports.typesAr.${reportType}`, { defaultValue: title }),
      periodLabel: `${periodStart} — ${periodEnd}`,
      htmlBody: printRef.current?.innerHTML ?? reportHtmlForPrint(data)
    });
  }

  const isFavorite = favorites.includes(reportType);

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.reports')}</h1>
      <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('reports.subtitle')}</p>

      <div className="mt-6 grid gap-4 rounded-lg border border-[var(--fv-border)] p-4 md:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1 text-sm">
          {t('reports.reportType')}
          <Select
            value={reportType}
            onValueChange={(v) => setReportType(v as ReportType)}
            options={reportTypes.map((r) => ({
              value: r.id,
              label: `${favorites.includes(r.id) ? '★ ' : ''}${t(r.labelKey)}`
            }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t('reports.startDate')}
          <DatePicker value={startDate} onChange={setStartDate} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t('reports.endDate')}
          <DatePicker value={endDate} onChange={setEndDate} min={startDate} />
        </label>
        {needsParty && (
          <label className="flex flex-col gap-1 text-sm">
            {reportType === 'customer_statement' ? t('sales.customer') : t('purchases.vendor')}
            <Select
              value={partyId}
              onValueChange={setPartyId}
              placeholder="—"
              options={[
                { value: '', label: '—' },
                ...partyPage.selectOptions
              ]}
            />
            <Pagination
              className="mt-2 border-0 px-0"
              pageIndex={partyPage.pageIndex}
              hasNext={partyPage.hasNext}
              hasPrev={partyPage.hasPrev}
              onNext={partyPage.nextPage}
              onPrev={partyPage.prevPage}
              loading={partyPage.loading}
            />
          </label>
        )}
        {reportType === 'journal_register' && (
          <label className="flex flex-col gap-1 text-sm">
            {t('reports.source')}
            <Select
              value={source}
              onValueChange={(v) => setSource(v as typeof source)}
              options={[
                { value: 'all', label: t('reports.sourceAll') },
                { value: 'manual', label: t('reports.sourceManual') },
                { value: 'opening', label: t('reports.sourceOpening') },
                { value: 'import', label: t('reports.sourceImport') }
              ]}
            />
          </label>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={() => {
            setGlPage(1);
            void generate(1);
          }}
          disabled={!online || loading}
        >
          {loading ? t('reports.generating') : t('reports.generate')}
        </Button>
        <Button variant="secondary" onClick={() => void toggleFavorite()}>
          {isFavorite ? t('reports.unfavorite') : t('reports.favorite')}
        </Button>
        {data && (
          <>
            <Button variant="secondary" onClick={exportCsv}>
              {t('reports.exportCsv')}
            </Button>
            <Button variant="secondary" onClick={exportPdf}>
              {t('reports.exportPdf')}
            </Button>
          </>
        )}
      </div>

      {cached && data && (
        <p className="mt-2 text-xs text-[var(--fv-text-muted)]">{t('reports.cached')}</p>
      )}
      {data && (
        <div
          ref={printRef}
          className="mt-6 overflow-x-auto rounded-lg border border-[var(--fv-border)] p-4"
        >
          <ReportView
            data={data}
            loading={loading}
            onGlPageChange={(p) => {
              setGlPage(p);
              void generate(p);
            }}
          />
        </div>
      )}
    </div>
  );
}
